// src/util/googleMaps.js
import { types as sdkTypes } from '../util/sdkLoader';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

const isDev = process.env.NODE_ENV === 'development';

/* ------------------------------------------------------------------ */
/* ✅ Locale-aware Google Maps loader (EN / TH) + SessionToken safe      */
/* ------------------------------------------------------------------ */

let googleMapsScriptPromise = null;
let currentSessionToken = null;

const normalizeMapsLang = locale => ((locale || 'en').toLowerCase() === 'th' ? 'th' : 'en');

const getLocaleFromLocalStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem('locale');
};

const getMapsLanguage = () => normalizeMapsLang(getLocaleFromLocalStorage() || 'en');

const getMapsApiKey = () =>
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  process.env.REACT_APP_GOOGLE_API_KEY ||
  process.env.REACT_APP_GOOGLE_KEY;

export const resetAutocompleteSessionToken = () => {
  currentSessionToken = null;
};

export const createAutocompleteSessionToken = () => {
  if (!window?.google?.maps?.places?.AutocompleteSessionToken) return null;
  return new window.google.maps.places.AutocompleteSessionToken();
};

export const getAutocompleteSessionToken = () => {
  // IMPORTANT: token must belong to the CURRENT window.google runtime.
  // If google maps script reloads (language switch), resetAutocompleteSessionToken() is called.
  if (!currentSessionToken) {
    currentSessionToken = createAutocompleteSessionToken();
  }
  return currentSessionToken;
};

const removeExistingGoogleMapsScript = () => {
  if (typeof document === 'undefined') return;

  const existing = document.querySelector('script[data-google-maps-script="true"]');
  if (existing) existing.remove();

  // reset global (so we can reload with a new language)
  if (typeof window !== 'undefined' && window.google) {
    try {
      delete window.google;
    } catch (e) {
      window.google = undefined;
    }
  }
};

const buildGoogleMapsUrl = ({ apiKey, language, region, libraries = ['places'] }) => {
  const params = new URLSearchParams({
    key: apiKey,
    v: 'weekly',
    libraries: libraries.join(','),
    language: language || 'en',
  });

  if (region) params.set('region', region);

  return `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
};

/**
 * Loads (or reloads) Google Maps JS API with language=EN/TH.
 * If language changes, it reloads the script.
 */
export const loadGoogleMaps = ({ language, region } = {}) => {
  if (typeof window === 'undefined') return Promise.resolve(null);

  const apiKey = getMapsApiKey();
  if (!apiKey) {
    const err = new Error('Missing Google Maps API key (REACT_APP_GOOGLE_MAPS_API_KEY).');
    if (isDev) console.error(err);
    return Promise.reject(err);
  }

  const normalizedLang = normalizeMapsLang(language || getMapsLanguage());
  const currentLang = window.__GOOGLE_MAPS_LANG__;
  const isAlreadyLoaded = !!(window.google && window.google.maps);

  // already loaded with same language
  if (isAlreadyLoaded && currentLang === normalizedLang) {
    return Promise.resolve(window.google);
  }

  // loaded but language changed => reload script
  if (isAlreadyLoaded && currentLang !== normalizedLang) {
    // ✅ critical: old AutocompleteSessionToken becomes invalid after reload
    resetAutocompleteSessionToken();

    removeExistingGoogleMapsScript();
    googleMapsScriptPromise = null;
  }

  // a load is already in flight
  if (googleMapsScriptPromise) return googleMapsScriptPromise;

  window.__GOOGLE_MAPS_LANG__ = normalizedLang;

  googleMapsScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.setAttribute('data-google-maps-script', 'true');
    script.async = true;
    script.defer = true;
    script.src = buildGoogleMapsUrl({
      apiKey,
      language: normalizedLang,
      region,
      libraries: ['places'],
    });

    script.onload = () => resolve(window.google);
    script.onerror = err => {
      googleMapsScriptPromise = null;
      reject(err);
    };

    document.head.appendChild(script);
  });

  return googleMapsScriptPromise;
};

const ensureGoogleMapsReady = async () => {
  if (typeof window === 'undefined') return;

  const lang = getMapsLanguage();

  // if already loaded in same lang, no-op
  if (window.google && window.google.maps && window.__GOOGLE_MAPS_LANG__ === lang) return;

  await loadGoogleMaps({ language: lang });
};

/* ------------------------------------------------------------------ */
/* ✅ Place helpers (old + new Places API)                              */
/* ------------------------------------------------------------------ */

const placeOrigin = place => {
  if (place && place.geometry && place.geometry.location) {
    return new SDKLatLng(place.geometry.location.lat(), place.geometry.location.lng());
  }
  return null;
};

/**
 * Extracts the geographic location (origin) from a Google Maps Place object
 * using the new Google Places API structure.
 */
const placeOriginNew = place => {
  if (place && place.location) {
    return new SDKLatLng(place.location.lat(), place.location.lng());
  }
  return null;
};

const placeBounds = place => {
  if (place && place.geometry && place.geometry.viewport) {
    const ne = place.geometry.viewport.getNorthEast();
    const sw = place.geometry.viewport.getSouthWest();
    return new SDKLatLngBounds(
      new SDKLatLng(ne.lat(), ne.lng()),
      new SDKLatLng(sw.lat(), sw.lng())
    );
  }
  return null;
};

/**
 * Extracts the viewport bounds from a Google Maps Place object using the new Places API.
 */
const placeBoundsNew = place => {
  if (place && place.viewport) {
    const ne = place.viewport.getNorthEast();
    const sw = place.viewport.getSouthWest();
    return new SDKLatLngBounds(
      new SDKLatLng(ne.lat(), ne.lng()),
      new SDKLatLng(sw.lat(), sw.lng())
    );
  }
  return null;
};

/**
 * Get a detailed place object (OLD PlacesService.getDetails)
 *
 * NOTE: sessionToken param is intentionally ignored to prevent passing a stale token instance.
 * We always generate token from the current window.google instance.
 */
export const getPlaceDetails = (placeId, _sessionToken) =>
  new Promise((resolve, reject) => {
    ensureGoogleMapsReady()
      .then(() => {
        const serviceStatus = window.google.maps.places.PlacesServiceStatus;
        const el = document.createElement('div');
        const service = new window.google.maps.places.PlacesService(el);
        const fields = ['address_component', 'formatted_address', 'geometry', 'place_id'];

        service.getDetails({ placeId, fields }, (place, status) => {
          if (status !== serviceStatus.OK) {
            reject(
              new Error(
                `Could not get details for place id "${placeId}", error status was "${status}"`
              )
            );
          } else {
            resolve({
              address: place.formatted_address,
              origin: placeOrigin(place),
              bounds: placeBounds(place),
            });
          }
        });
      })
      .catch(reject);
  });

/**
 * Get a detailed place object (NEW Place.fetchFields)
 */
export const getPlaceDetailsNew = async placeId => {
  try {
    await ensureGoogleMapsReady();

    const place = await new window.google.maps.places.Place({ id: placeId });
    const fields = ['addressComponents', 'formattedAddress', 'viewport', 'id', 'location'];

    await place.fetchFields({ fields });

    return {
      address: place.formattedAddress,
      origin: placeOriginNew(place),
      bounds: placeBoundsNew(place),
    };
  } catch (error) {
    if (isDev) console.error(`Could not get details for place id "${placeId}": `, error);
    return error;
  }
};

const predictionSuccessful = status => {
  const { OK, ZERO_RESULTS } = window.google.maps.places.PlacesServiceStatus;
  return status === OK || status === ZERO_RESULTS;
};

/**
 * Get place predictions (OLD AutocompleteService.getPlacePredictions)
 *
 * NOTE: sessionToken param is intentionally ignored to prevent passing a stale token instance.
 * We always generate token from the current window.google instance.
 */
export const getPlacePredictions = (search, _sessionToken, searchConfigurations) =>
  new Promise((resolve, reject) => {
    ensureGoogleMapsReady()
      .then(() => {
        const service = new window.google.maps.places.AutocompleteService();

        // ✅ token must be created from CURRENT google maps runtime
        const sessionToken = getAutocompleteSessionToken();
        const sessionTokenMaybe = sessionToken ? { sessionToken } : {};

        service.getPlacePredictions(
          { input: search, ...sessionTokenMaybe, ...searchConfigurations },
          (predictions, status) => {
            if (!predictionSuccessful(status)) {
              reject(new Error(`Prediction service status not OK: ${status}`));
            } else {
              resolve({
                search,
                predictions: predictions || [],
              });
            }
          }
        );
      })
      .catch(reject);
  });

/**
 * Get place predictions (NEW AutocompleteSuggestion.fetchAutocompleteSuggestions)
 *
 * NOTE: sessionToken param is intentionally ignored to prevent passing a stale token instance.
 * We always generate token from the current window.google instance.
 */
export const getPlacePredictionsNew = async (search, _sessionToken, searchConfigurations) => {
  try {
    await ensureGoogleMapsReady();

    // ✅ token must be created from CURRENT google maps runtime
    const sessionToken = getAutocompleteSessionToken();
    const sessionTokenMaybe = sessionToken ? { sessionToken } : {};

    const request = {
      input: search,
      ...searchConfigurations,
      ...sessionTokenMaybe,
    };

    const { suggestions } =
      await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    return {
      search,
      predictions: suggestions || [],
    };
  } catch (error) {
    if (isDev) {
      console.error(`Could not get autocomplete suggestions using search query "${search}": `, error);
    }
    return error;
  }
};

/**
 * Call this when user selects a place (end of autocomplete session).
 * Example: after getPlaceDetails/getPlaceDetailsNew resolves.
 */
export const endAutocompleteSession = () => {
  resetAutocompleteSessionToken();
};

/* ------------------------------------------------------------------ */
/* Existing helpers (unchanged)                                         */
/* ------------------------------------------------------------------ */

/**
 * Deprecation: use function from src/util/maps.js
 * Cut some precision from bounds coordinates
 */
export const sdkBoundsToFixedCoordinates = (sdkBounds, fixedPrecision) => {
  const fixed = n => Number.parseFloat(n.toFixed(fixedPrecision));
  const ne = new SDKLatLng(fixed(sdkBounds.ne.lat), fixed(sdkBounds.ne.lng));
  const sw = new SDKLatLng(fixed(sdkBounds.sw.lat), fixed(sdkBounds.sw.lng));

  return new SDKLatLngBounds(ne, sw);
};

/**
 * Deprecation: use function from src/util/maps.js
 * Check if given bounds object have the same coordinates
 */
export const hasSameSDKBounds = (sdkBounds1, sdkBounds2) => {
  if (!(sdkBounds1 instanceof SDKLatLngBounds) || !(sdkBounds2 instanceof SDKLatLngBounds)) {
    return false;
  }
  return (
    sdkBounds1.ne.lat === sdkBounds2.ne.lat &&
    sdkBounds1.ne.lng === sdkBounds2.ne.lng &&
    sdkBounds1.sw.lat === sdkBounds2.sw.lat &&
    sdkBounds1.sw.lng === sdkBounds2.sw.lng
  );
};

/**
 * Calculate a bounding box in the given location
 */
export const locationBounds = (latlng, distance) => {
  const bounds = new window.google.maps.Circle({
    center: new window.google.maps.LatLng(latlng.lat, latlng.lng),
    radius: distance,
  }).getBounds();

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  return new SDKLatLngBounds(new SDKLatLng(ne.lat(), ne.lng()), new SDKLatLng(sw.lat(), sw.lng()));
};

/**
 * Helper functions for handling Google OverlayView.
 */
export const getOffsetOverride = (containerElement, props) => {
  const { getPixelPositionOffset } = props;
  if (typeof getPixelPositionOffset === 'function') {
    return getPixelPositionOffset(containerElement.offsetWidth, containerElement.offsetHeight);
  }
  return {};
};

const getLayoutStylesByBounds = (mapCanvasProjection, offset, bounds) => {
  const ne = mapCanvasProjection.fromLatLngToDivPixel(bounds.getNorthEast());
  const sw = mapCanvasProjection.fromLatLngToDivPixel(bounds.getSouthWest());
  if (ne && sw) {
    return {
      left: `${sw.x + offset.x}px`,
      top: `${ne.y + offset.y}px`,
      width: `${ne.x - sw.x - offset.x}px`,
      height: `${sw.y - ne.y - offset.y}px`,
    };
  }
  return { left: `-9999px`, top: `-9999px` };
};

const getLayoutStylesByPosition = (mapCanvasProjection, offset, position) => {
  const point = mapCanvasProjection.fromLatLngToDivPixel(position);
  if (point) {
    const { x, y } = point;
    return { left: `${x + offset.x}px`, top: `${y + offset.y}px` };
  }
  return { left: `-9999px`, top: `-9999px` };
};

export const getLayoutStyles = (mapCanvasProjection, offset, props) => {
  const createLatLng = (inst, Type) => new Type(inst.lat, inst.lng);

  const createLatLngBounds = (inst, Type) =>
    new Type(
      new window.google.maps.LatLng(inst.ne.lat, inst.ne.lng),
      new window.google.maps.LatLng(inst.sw.lat, inst.sw.lng)
    );

  const ensureOfType = (inst, type, factory) => {
    if (inst instanceof type) return inst;
    return factory(inst, type);
  };

  if (props.bounds) {
    const bounds = ensureOfType(props.bounds, window.google.maps.LatLngBounds, createLatLngBounds);
    return getLayoutStylesByBounds(mapCanvasProjection, offset, bounds);
  }

  const position = ensureOfType(props.position, window.google.maps.LatLng, createLatLng);
  return getLayoutStylesByPosition(mapCanvasProjection, offset, position);
};
